import type { CardRole } from '../card-roles';

export type RoleDescriptor = {
    label: string;
    lines: string[];
};

export const ROLE_DESCRIPTORS: Record<CardRole, RoleDescriptor> = {
    UncategorisedCard: {
        label: 'Arcana Card',
        lines: [
            'A card drawn from the universal deck.',
        ],
    },
    PersonalityCard: {
        label: 'Your Personality Card',
        lines: [
            'The outward face you present to the world — the mask and the mirror.',
            'This card reveals the persona shaped by your date of birth.',
            'Derived from the numerological reduction of your birthday.',
        ],
    },
    CharacterCard: {
        label: 'Your Character Card',
        lines: [
            'The inner nature beneath the personality — your deeper motivations and drives.',
            'Where the Personality Card is the mask, this is the face behind it.',
            'Derived from your specific celestial data.',
        ],
    },
    HybridCharacterPersonalCard: {
        label: 'Your Unified Persona Card',
        lines: [
            'When the personality and the character become one — outer mask and inner face aligned.',
            'Your date of birth reduces to a single essence: the same card speaks for both the persona you present and the nature beneath it.',
            'A unified persona — what you show the world is precisely who you are within.',
        ],
    },
    GrowthCard: {
        label: 'Your Growth Card',
        lines: [
            'The card of your current cycle — the lesson the universe is teaching you this year.',
            'Growth cards shift annually, marking the evolving chapter of your journey.',
            'Derived from your birth date and the current year.',
        ],
    },
    FirstNameCard: {
        label: 'Your First Name Card',
        lines: [
            'The name you are called by every day — the frequency your world knows you by.',
            'This card is your conscious self: your ego, your immediate personality, and the personal worldview through which you perceive everything.',
            'It is the active, driving force of this incarnation — your own path through this life, independent of where you came from.',
        ],
    },
    MiddleNameCard: {
        label: 'Your Middle Name Card',
        lines: [
            'The hidden name — the bridge between your outer self and your family lineage.',
            'Your middle name reveals qualities held in reserve, waiting to be called upon.',
            'Spoken rarely and known to few, it describes the private self that endures beneath every role and expectation.',
        ],
    },
    LastNameCard: {
        label: 'Your Last Name Card',
        lines: [
            'Your surname carries the energetic weight of the generations who came before you.',
            'This card is the foundation you were planted in — the psychic soil of your bloodline, its ancestral history, strengths, and generational karma.',
            'It reveals the repeating patterns passed down to you; when it clashes with your First Name Card, it often marks a life spent breaking from family expectation or healing inherited wounds.',
        ],
    },
    WholeNameCard: {
        label: 'Your Whole Name Card',
        lines: [
            'The Expression Card — the grand synthesis of your complete name.',
            'It shows who you are (your First Name) acting upon what you inherited (your Family Name), fused into a single cosmic tone.',
            'This is the overarching vehicle you operate to navigate the world — the total identity you carry through this incarnation.',
        ],
    },
    ZodiacalSunCard: {
        label: 'Your Zodiacal Sun Card',
        lines: [
            'The Zodiacal Sun is your cosmic archetype.',
            'Behold the colour of your soul. This card represents the very nature of your being, how you live and your role to play.',
            'Your Zodiacal Sun is based on your Zodiac. This is the part of the universe which is you.',
        ],
    },
    RisingSunCard: {
        label: 'Your Rising Sun Card',
        lines: [
            'The Rising Sun — your ascendant influence, how the world first perceives you.',
            'This card colours the dawn of every encounter and first impression.',
            'Derived from your birth time and location.',
        ],
    },
    PersonalZodiacalCard: {
        label: 'Your Personal Zodiacal Card',
        lines: [
            'The Zodiacal Sun is your cosmic archetype.',
            'Behold the colour of your soul. This card represents the very nature of your being, how you live and your role to play.',
            'Your Zodiacal Sun is based on your Zodiac. This is the part of the universe which is you.',
        ],
    },
    PersonalDecanCard: {
        label: 'Your Decan Card',
        lines: [
            'A refinement of your Zodiacal Sun — the aspect and flavour of your soul.',
            'Reveals how your archetype manifests through you, the particular expression of your cosmic nature.',
            'Your Decan is based on the specific third of your Zodiac.',
        ],
    },
    PersonalCourtCard: {
        label: 'Your Court Card',
        lines: [
            'The culmination of your being — who you truly are, or else may reveal yourself as and become.',
            'This is you at your deepest core, your truest potential.',
            'Your Court Card is based on the precise degree of your Zodiac.',
        ],
    },
};
